import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, Box, Button, Card, CardContent, Chip, Container, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import sessionStore from '../stores/sessionStore'
import organizationSessionStore from '../stores/organizationSessionStore'
import organizationProfileStore from '../stores/organizationProfileStore'
import {
	membershipProfileCapabilities,
	normalizeMembershipContacts,
	normalizePublicContacts,
	organizationProfileCapabilities,
} from '../tenancy/organizationProfilePolicy'
import { normalizeVisibilityPolicy, visibilityLabel } from '../tenancy/resourceAssignmentPolicy'
import { membershipRoleLabel } from '../tenancy/organizationPresentation'

const organizationLabel = organization => organization?.kind === 'PERSONAL' ? 'Личное' : organization?.name || 'Организация'

function contactsToText(contacts, visibility = false) {
	return (contacts || []).map(contact => visibility
		? `${contact.type}|${contact.isPublic ? 'public' : 'private'}|${contact.value}`
		: `${contact.type}|${contact.value}`).join('\n')
}

function contactsFromText(value, visibility = false) {
	const rows = String(value || '').split(/\r?\n/).map(row => row.trim()).filter(Boolean)
	const contacts = rows.map(row => {
		const parts = row.split('|').map(part => part.trim())
		if (visibility && !['public', 'private'].includes(parts[1])) throw new TypeError('visibility')
		return visibility
			? { type: parts[0], isPublic: parts[1] === 'public', value: parts.slice(2).join('|') }
			: { type: parts[0], value: parts.slice(1).join('|') }
	})
	return visibility ? normalizeMembershipContacts(contacts) : normalizePublicContacts(contacts)
}

function ContactList({ contacts }) {
	if (!contacts?.length) return <Typography color='text.secondary'>Контакты не указаны</Typography>
	return <Stack spacing={0.5} component='ul' sx={{ pl: 2, mb: 0 }}>{contacts.map((contact, index) => (
		<Typography component='li' key={`${contact.type}-${contact.value}-${index}`}>
			{contact.type}: {contact.value}{contact.isPublic === false ? ' (непубличный)' : ''}
		</Typography>
	))}</Stack>
}

function OrganizationProfileEditor({ profile }) {
	const canEdit = organizationProfileCapabilities(profile.organization, organizationSessionStore.membershipRole).canEditPresentation
	const [name, setName] = useState(profile.organization.name || '')
	const [contacts, setContacts] = useState(contactsToText(profile.organization.publicContacts))
	const [validationError, setValidationError] = useState(null)

	useEffect(() => {
		setName(profile.organization.name || '')
		setContacts(contactsToText(profile.organization.publicContacts))
		setValidationError(null)
	}, [profile])

	const submit = async event => {
		event.preventDefault()
		try {
			if (!name.trim()) throw new TypeError('name')
			const publicContacts = contactsFromText(contacts)
			setValidationError(null)
			await organizationProfileStore.updateOrganization({ name: name.trim(), publicContacts })
		} catch {
			setValidationError('Укажите название и контакты строками TYPE|значение.')
		}
	}

	return <Card variant='outlined'><CardContent>
		<Stack spacing={2} component={canEdit ? 'form' : 'div'} onSubmit={canEdit ? submit : undefined}>
			<Box>
				<Typography variant='h6'>{organizationLabel(profile.organization)}</Typography>
				<Typography color='text.secondary'>Reldo ID: {profile.organization.reldoId}</Typography>
			</Box>
			{canEdit ? <>
				<TextField label='Название агентства' value={name} onChange={event => setName(event.target.value)} required inputProps={{ maxLength: 200 }} />
				<TextField label='Публичные контакты' value={contacts} onChange={event => setContacts(event.target.value)} multiline minRows={3} helperText='Один контакт на строку: TYPE|значение. Например: PHONE|+79990000000' />
				{profile.organization.logoObjectKey
					? <Button color='warning' onClick={() => organizationProfileStore.updateOrganization({ logoObjectKey: null })} disabled={organizationProfileStore.saving}>Удалить логотип</Button>
					: <Typography color='text.secondary'>Загрузка логотипа пока недоступна; профиль не принимает произвольный URL или ключ хранилища.</Typography>}
				{validationError && <Alert severity='warning'>{validationError}</Alert>}
				<Button type='submit' variant='contained' disabled={organizationProfileStore.saving}>Сохранить профиль организации</Button>
			</> : <ContactList contacts={profile.organization.publicContacts} />}
			{profile.organization.logoObjectKey && <Typography color='text.secondary'>Логотип настроен</Typography>}
		</Stack>
	</CardContent></Card>
}

function OrganizationVisibilityEditor({ organization }) {
	const context = organizationSessionStore.currentContext
	const policies = context?.policies
	const canEdit = organization?.kind === 'TEAM' && ['OWNER', 'ADMIN'].includes(context?.membership?.role)
	const [requestVisibility, setRequestVisibility] = useState(policies?.requestVisibility || 'ASSIGNED_ONLY')
	const [clientVisibility, setClientVisibility] = useState(policies?.clientVisibility || 'ASSIGNED_ONLY')

	useEffect(() => {
		setRequestVisibility(policies?.requestVisibility || 'ASSIGNED_ONLY')
		setClientVisibility(policies?.clientVisibility || 'ASSIGNED_ONLY')
	}, [policies?.requestVisibility, policies?.clientVisibility])

	if (!policies) return null
	if (!canEdit) return <Card variant='outlined'><CardContent><Typography variant='h6'>Видимость рабочих данных</Typography><Typography>Заявки: {visibilityLabel(policies.requestVisibility)}</Typography><Typography>Клиенты: {visibilityLabel(policies.clientVisibility)}</Typography></CardContent></Card>
	const save = async event => {
		event.preventDefault()
		await organizationProfileStore.updateVisibilityPolicies({
		requestVisibility: normalizeVisibilityPolicy(requestVisibility),
		clientVisibility: normalizeVisibilityPolicy(clientVisibility),
		})
	}
	return <Card variant='outlined'><CardContent><Stack component='form' spacing={2} onSubmit={save}>
		<Box><Typography variant='h6'>Видимость рабочих данных</Typography><Typography color='text.secondary'>Настройки применяет сервер ко всем рабочим и архивным заявкам и ко всем клиентам. Они не фильтруют уже загруженные данные в браузере.</Typography></Box>
		<FormControl><InputLabel id='request-visibility-label'>Заявки</InputLabel><Select labelId='request-visibility-label' label='Заявки' value={requestVisibility} onChange={event => setRequestVisibility(event.target.value)}><MenuItem value='ASSIGNED_ONLY'>Только назначенный участник</MenuItem><MenuItem value='ALL_MEMBERS'>Все активные участники</MenuItem></Select></FormControl>
		<FormControl><InputLabel id='client-visibility-label'>Клиенты</InputLabel><Select labelId='client-visibility-label' label='Клиенты' value={clientVisibility} onChange={event => setClientVisibility(event.target.value)}><MenuItem value='ASSIGNED_ONLY'>Только назначенный участник</MenuItem><MenuItem value='ALL_MEMBERS'>Все активные участники</MenuItem></Select></FormControl>
		<Button type='submit' variant='contained' disabled={organizationProfileStore.saving}>Сохранить видимость</Button>
	</Stack></CardContent></Card>
}

function MembershipProfileCard({ membership, organizationKind }) {
	const actor = organizationSessionStore.currentContext?.membership
	const capabilities = membershipProfileCapabilities({
		organizationKind,
		actorMembershipId: actor?.id,
		actorRole: actor?.role,
		targetMembership: membership,
	})
	const [editing, setEditing] = useState(false)
	const [displayName, setDisplayName] = useState(membership.displayName || '')
	const [title, setTitle] = useState(membership.title || '')
	const [contacts, setContacts] = useState(contactsToText(membership.contacts, true))
	const [validationError, setValidationError] = useState(null)
	const canEdit = Object.values(capabilities).some(Boolean)

	useEffect(() => {
		setDisplayName(membership.displayName || '')
		setTitle(membership.title || '')
		setContacts(contactsToText(membership.contacts, true))
		setValidationError(null)
	}, [membership])

	const submit = async event => {
		event.preventDefault()
		try {
			const patch = {}
			if (capabilities.canEditDisplayName) patch.displayName = displayName.trim() || null
			if (capabilities.canEditTitle) patch.title = title.trim() || null
			if (capabilities.canEditContacts) patch.contacts = contactsFromText(contacts, true)
			setValidationError(null)
			if (await organizationProfileStore.updateMembership(membership.id, patch)) setEditing(false)
		} catch {
			setValidationError('Контакты задаются строками TYPE|public/private|значение.')
		}
	}

	return <Card variant='outlined'><CardContent>
		<Stack spacing={1.5} component={editing ? 'form' : 'div'} onSubmit={editing ? submit : undefined}>
			<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
				<Typography variant='h6'>{membership.displayName || 'Профессиональный профиль'}</Typography>
				<Chip size='small' label={membershipRoleLabel(membership.role)} />
				{String(actor?.id) === membership.id && <Chip size='small' color='primary' label='Вы' />}
			</Stack>
			{editing ? <>
				{capabilities.canEditDisplayName && <TextField label='Отображаемое имя' value={displayName} onChange={event => setDisplayName(event.target.value)} inputProps={{ maxLength: 200 }} />}
				{capabilities.canEditTitle && <TextField label='Должность' value={title} onChange={event => setTitle(event.target.value)} inputProps={{ maxLength: 200 }} />}
				{capabilities.canEditContacts && <TextField label='Контакты участника' value={contacts} onChange={event => setContacts(event.target.value)} multiline minRows={3} helperText='TYPE|public|значение или TYPE|private|значение' />}
				{validationError && <Alert severity='warning'>{validationError}</Alert>}
				<Stack direction='row' spacing={1}>
					<Button type='submit' variant='contained' disabled={organizationProfileStore.saving}>Сохранить</Button>
					<Button onClick={() => setEditing(false)} disabled={organizationProfileStore.saving}>Отмена</Button>
				</Stack>
			</> : <>
				<Typography>{membership.title || 'Должность не указана'}</Typography>
				<ContactList contacts={membership.contacts} />
				{membership.avatarObjectKey && <Typography color='text.secondary'>Аватар настроен</Typography>}
				{capabilities.canEditAvatar && membership.avatarObjectKey && <Button color='warning' size='small' onClick={() => organizationProfileStore.updateMembership(membership.id, { avatarObjectKey: null })}>Удалить аватар</Button>}
				{capabilities.canEditAvatar && !membership.avatarObjectKey && <Typography color='text.secondary'>Загрузка аватара пока недоступна.</Typography>}
				{canEdit && <Button size='small' onClick={() => setEditing(true)}>Редактировать доступные поля</Button>}
			</>}
		</Stack>
	</CardContent></Card>
}

function DiscoveryPanel() {
	const [reldoId, setReldoId] = useState('')
	const [email, setEmail] = useState('')
	const result = organizationProfileStore.discoveryResult
	return <Card variant='outlined'><CardContent><Stack spacing={2}>
		<Box>
			<Typography variant='h5'>Точный поиск организации</Typography>
			<Typography color='text.secondary'>Поиск выполняется только после отправки полного Reldo ID или адреса электронной почты. Подсказки и частичное совпадение не используются.</Typography>
		</Box>
		<Grid container spacing={2}>
			<Grid item xs={12} md={6}><Stack component='form' direction={{ xs: 'column', sm: 'row' }} spacing={1} onSubmit={event => { event.preventDefault(); organizationProfileStore.discover('reldoId', reldoId) }}>
				<TextField label='Reldo ID' value={reldoId} onChange={event => setReldoId(event.target.value)} inputProps={{ inputMode: 'numeric', maxLength: 12 }} fullWidth />
				<Button type='submit' variant='contained' disabled={organizationProfileStore.discovering}>Найти</Button>
			</Stack></Grid>
			<Grid item xs={12} md={6}><Stack component='form' direction={{ xs: 'column', sm: 'row' }} spacing={1} onSubmit={event => { event.preventDefault(); organizationProfileStore.discover('email', email) }}>
				<TextField label='Электронная почта владельца личной организации' type='email' value={email} onChange={event => setEmail(event.target.value)} fullWidth />
				<Button type='submit' variant='contained' disabled={organizationProfileStore.discovering}>Найти</Button>
			</Stack></Grid>
		</Grid>
		{result && <Box aria-live='polite'>
			<Divider sx={{ mb: 2 }} />
			<Typography variant='h6'>{organizationLabel(result.organization)}</Typography>
			<Typography>Reldo ID: {result.organization.reldoId}</Typography>
			{result.professional && <Typography>{result.professional.displayName || 'Профессиональный профиль'}{result.professional.title ? ` — ${result.professional.title}` : ''}</Typography>}
			<ContactList contacts={result.organization.publicContacts} />
			{result.professional && <ContactList contacts={result.professional.contacts} />}
			<Alert severity='info' sx={{ mt: 2 }}>Организация найдена. Настройка доступа к объектам выполняется отдельными средствами распространения.</Alert>
		</Box>}
	</Stack></CardContent></Card>
}

const MembersPage = observer(() => {
	const generation = organizationSessionStore.contextGeneration
	useEffect(() => { organizationProfileStore.load() }, [generation])
	const profile = organizationProfileStore.profile
	const accountEmail = sessionStore.user?.email || ''
	return <Container maxWidth='lg' sx={{ py: 4 }}><Stack spacing={3}>
		<Box>
			<Typography variant='h4' component='h1'>Профили и поиск</Typography>
			<Typography color='text.secondary'>Аккаунт отвечает за вход, организация — за внешний профиль, профиль участника — за профессиональное представление в выбранном контексте.</Typography>
		</Box>
		<Card variant='outlined'><CardContent>
			<Typography variant='h6'>Аккаунт</Typography>
			<Typography>{accountEmail}</Typography>
			<Typography color='text.secondary'>Электронная почта используется для аутентификации. Профессиональные имя, должность, аватар и контакты хранятся в профиле участника.</Typography>
		</CardContent></Card>
		{organizationProfileStore.error && <Alert severity='error'>{organizationProfileStore.error}</Alert>}
		{organizationProfileStore.loading && <Typography>Загрузка профилей…</Typography>}
		{profile && <>
			<OrganizationProfileEditor profile={profile} />
			<OrganizationVisibilityEditor organization={profile.organization} />
			<Box><Typography variant='h5' sx={{ mb: 2 }}>Профили участников</Typography><Grid container spacing={2}>
				{profile.memberships.map(membership => <Grid item xs={12} md={6} key={membership.id}><MembershipProfileCard membership={membership} organizationKind={profile.organization.kind} /></Grid>)}
			</Grid></Box>
		</>}
		<DiscoveryPanel />
	</Stack></Container>
})

export default MembersPage
